import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Landmark, RefreshCw } from "lucide-react";
import api from "../api/axios";
import { UserContext } from "../context/UserContext";

const statusStyles = {
  credited: "bg-emerald-500/15 border border-emerald-400/30 text-emerald-300",
  pending: "bg-amber-500/15 border border-amber-400/30 text-amber-300",
  failed: "bg-rose-500/15 border border-rose-400/30 text-rose-300",
};

const statusLabels = {
  credited: "Credited",
  pending: "Pending",
  failed: "Failed",
};

function formatCurrency(amount) {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return "₦0";
  return `₦${numeric.toLocaleString()}`;
}

export default function MekHubPage() {
  const { user } = useContext(UserContext);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const userId = user?.id || "";

  const loadPayouts = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get("/payouts", { params: { userId } });
      setPayouts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Unable to load payouts", error);
      setPayouts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userId]);

  useEffect(() => {
    setLoading(true);
    void loadPayouts();
  }, [loadPayouts]);

  const latestPayout = payouts[0] || null;
  const totalPayout = useMemo(
    () => payouts.reduce((sum, item) => sum + (Number(item?.amount) || 0), 0),
    [payouts]
  );

  return (
    <div className="space-y-6 text-white">
      <div className="relative overflow-hidden rounded-[2rem] border border-cyan-300/20 bg-slate-900/85 p-6">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(6,182,212,0.22),_transparent_56%)]" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.24em] text-cyan-300">MekHub</p>
            <h1 className="mt-2 text-3xl font-bold">Prop-firm payouts and wallet activity</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-300">
              Track MekFunded payouts in one place, including payout status, reference, and wallet impact.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              setRefreshing(true);
              void loadPayouts();
            }}
            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-100 transition hover:bg-white/10"
            disabled={refreshing}
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-3xl border border-white/10 bg-slate-900/75 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Latest payout</p>
          <p className="mt-3 text-3xl font-bold text-cyan-300">
            {latestPayout ? formatCurrency(latestPayout.amount) : "₦0"}
          </p>
          <p className="mt-2 text-sm text-slate-400">Most recent credited amount</p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-slate-900/75 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Total payouts</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">{formatCurrency(totalPayout)}</p>
          <p className="mt-2 text-sm text-slate-400">Sum of all payouts on your account</p>
        </div>
        <div className="rounded-3xl border border-white/10 bg-slate-900/75 p-5">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Records</p>
          <p className="mt-3 text-3xl font-bold text-white">{payouts.length}</p>
          <p className="mt-2 text-sm text-slate-400">Payout entries synced from backend</p>
        </div>
      </div>

      <div className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6">
        <div className="mb-4 flex items-center gap-2">
          <Landmark size={18} className="text-cyan-300" />
          <h2 className="text-lg font-semibold">Recent payouts</h2>
        </div>

        {loading ? (
          <p className="text-slate-400">Loading payouts...</p>
        ) : payouts.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-slate-950/50 p-5">
            <p className="font-semibold text-white">No payouts yet</p>
            <p className="mt-1 text-sm text-slate-400">
              Once a payout is approved and credited, it will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {payouts.map((item) => {
              const rawStatus = String(item?.status || "").toLowerCase();
              const statusClass = statusStyles[rawStatus] || "bg-slate-800/70 border border-white/10 text-slate-200";
              const statusLabel = statusLabels[rawStatus] || "Updated";

              return (
                <div
                  key={item._id || item.reference}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-white/10 bg-slate-950/50 p-4"
                >
                  <div>
                    <p className="text-sm font-semibold text-white">
                      {item.source === "mekfunded" ? "MekFunded payout" : "Payout"}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">Ref: {item.reference || "N/A"}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : "Date unavailable"}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-base font-bold text-white">{formatCurrency(item.amount)}</p>
                    <span className={`mt-2 inline-block rounded-full px-3 py-1 text-xs font-semibold ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
